import { useMemo } from 'react';
import { useLocation, Outlet } from 'react-router';
import {
    LayoutGrid01,
    Atom02,
    Package,
    Users01,
    UserPlus01,
    BookOpen01,
    Settings01,
    HelpCircle,
    Bell01,
    ShoppingCart01,
    FileCheck02,
    File01,
    Globe02
} from '@untitledui/icons';
import { SidebarNavigationSimple } from '@/components/application/app-navigation/sidebar-navigation/sidebar-simple';
import { AICTACard } from '@/components/application/ai-chat/ai-cta-card';
import type { NavItemType } from '@/components/application/app-navigation/config';
import { useTranslation } from 'react-i18next';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';
import { MenuRouteGuard } from '@/components/application/route-guard/menu-route-guard';
import { useActivityUnreadCount } from '@/hooks/api/use-activities';

// Estendi NavItemType per includere key
type NavItemWithKey = NavItemType & { key?: string };

export const ShipperLayout = () => {
    const location = useLocation();
    const currentPath = location.pathname;
    const { t } = useTranslation('navigation');
    const { isMenuItemEnabled } = useMenuConfiguration();

    // Fetch unread count for activity badge
    const { data: unreadCount = 0 } = useActivityUnreadCount('shipper');

    // Navigation items for sidebar (con chiavi per il filtraggio)
    const allSidebarItems: NavItemWithKey[] = useMemo(() => [
        {
            key: 'ask-eve',
            label: t('askEve'),
            href: '/shipper/ask-eve',
            icon: Atom02
        },
        {
            key: 'dashboard',
            label: t('controlHub'),
            href: '/shipper/control-hub',
            icon: LayoutGrid01
        },
        {
            key: 'orders',
            label: t('orders'),
            href: '/shipper/orders',
            icon: ShoppingCart01
        },
        {
            key: 'quotes',
            label: t('quotes'),
            href: '/shipper/quotes',
            icon: FileCheck02
        },
        {
            key: 'containers',
            label: t('containers'),
            href: '/shipper/containers',
            icon: Package
        },
        {
            key: 'liveTracking',
            label: t('liveTracking'),
            href: '/shipper/live-tracking',
            icon: Globe02
        },
        {
            key: 'documents',
            label: t('eveDrive'),
            href: '/shipper/documents',
            icon: File01
        },
        {
            key: 'activity',
            label: t('activity'),
            href: '/shipper/activity',
            icon: Bell01,
            badge: unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-error-600 ring-1 ring-primary">
                    <span className="sr-only">{t('unreadNotifications', { count: unreadCount })}</span>
                </span>
            ) : undefined
        },
        {
            key: 'businessPartners',
            label: t('businessPartners'),
            href: '/shipper/business-partners',
            icon: Users01
        },
        {
            key: 'team',
            label: t('team'),
            href: '/shipper/team',
            icon: UserPlus01
        },
        {
            key: 'addressBook',
            label: t('addressBook'),
            href: '/shipper/address-book',
            icon: BookOpen01
        },
        {
            key: 'settings',
            label: t('settings'),
            href: '/shipper/profile',
            icon: Settings01
        }
    ], [t, unreadCount]);

    // Filtra i sidebar items in base alla configurazione menu
    const sidebarItems = useMemo(() =>
        allSidebarItems.filter(item =>
            !item.key || isMenuItemEnabled(item.key)
        ),
        [allSidebarItems, isMenuItemEnabled, unreadCount]
    );

    // Footer items for sidebar
    const footerItems: NavItemType[] = [
        {
            label: t('support'),
            href: '#',
            icon: HelpCircle
        },
    ];

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    // Nascondi "Summon Eve" quando si è sulla pagina Ask Eve
    const isOnAskEvePage = currentPath === '/shipper/ask-eve';

    // Mappa live-tracking: full-width e full-height (nessun padding/margin)
    const isOnLiveTrackingPage = currentPath === '/shipper/live-tracking' || currentPath === '/shipper/containers/world-map';

    const isFullPage = isOnAskEvePage || isOnLiveTrackingPage;

    return (
        <div className={`flex h-screen bg-secondary ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            {/* Sidebar Navigation */}
            <SidebarNavigationSimple
                activeUrl={currentPath}
                items={sidebarItems}
                footerItems={footerItems}
                featureCard={!isOnAskEvePage ? <AICTACard /> : undefined}
                showAccountCard={true}
                className="hidden lg:flex"
            />

            {/* Page Content */}
            <main className={`flex-1 min-h-0 bg-secondary ${isFullPage ? 'overflow-hidden' : 'overflow-auto'}`}>
                <div className={`${isFullPage ? 'h-full min-h-0 py-0' : 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6'}`}>
                    <MenuRouteGuard>
                        <Outlet />
                    </MenuRouteGuard>
                </div>
            </main>
        </div>
    );
};
